/**
 * Single source of truth for all application permissions (frontend mirror).
 *
 * This file mirrors the backend constants at:
 *   C-Back-End/src/common/constants/permissions.ts
 *
 * When adding a new permission, add it to BOTH files.
 *
 * Usage:
 *   import { PERMISSIONS } from '@/constants/permissions';
 *   authUtils.hasPermission(PERMISSIONS.CLIENT.DETAILS_VIEW)
 */

export const PERMISSIONS = {
  // ── Dashboard ─────────────────────────────────────────────
  DASHBOARD: {
    VIEW: 'perm-dashboard-view',
  },

  // ── Clients ───────────────────────────────────────────────
  // ASSIGN / REP_REASSIGN retired (Option B): use ASSIGNMENT.REASSIGN_REP.
  // Former perm-client-files-* and perm-client-archive folded into
  // DETAILS_VIEW (read/files view) / DETAILS_EDIT (mutate/archive).
  // Client notes + interactions fold into DETAILS_VIEW / DETAILS_EDIT.
  CLIENT: {
    CREATE: 'perm-client-create',
    DELETE: 'perm-client-delete',
    DETAILS_VIEW: 'perm-client-details-view',
    DETAILS_EDIT: 'perm-client-details-edit',
    // Advanced. Owner always. Nobody else by default.
    TRANSFER: 'perm-client-transfer',
  },

  // ── Intake Forms ──────────────────────────────────────────
  // Variations fold into INTAKE.VIEW / INTAKE.EDIT.
  INTAKE: {
    CREATE: 'perm-intake-create',
    VIEW: 'perm-intake-view',
    EDIT: 'perm-intake-edit',
    DELETE: 'perm-intake-delete',
  },

  // ── Architect Requests ────────────────────────────────────
  ARCHITECT: {
    REQUEST_RENDERING: 'perm-architect-request-rendering',
    REQUEST_PLANS: 'perm-architect-request-plans',
    REVIEW_REQUESTS: 'perm-architect-review-requests',
    APPROVE_DENY: 'perm-architect-approve-deny',
    RESPOND_QUOTE: 'perm-architect-respond-quote',
  },

  // ── Proposals ─────────────────────────────────────────────
  // Create / section-edit / delete folded into VIEW (read-only after finalize).
  PROPOSAL: {
    VIEW: 'perm-proposal-view',
  },

  // ── Impersonation ─────────────────────────────────────────
  IMPERSONATION: {
    ROLE: 'perm-impersonation-role',
    INDIVIDUAL: 'perm-impersonation-individual',
  },

  // ── Team ──────────────────────────────────────────────────
  // REASSIGN / DEACTIVATE folded into EDIT.
  TEAM: {
    VIEW: 'perm-team-view',
    INVITE: 'perm-team-invite',
    EDIT: 'perm-team-edit',
    DELETE: 'perm-team-delete',
  },

  // ── Settings ──────────────────────────────────────────────
  // COMPANY_OPEN / COMPANY_EDIT / COMPANY_UNIVERSAL / COMPANY_LOCATION /
  // COMPANY_INTAKE / COMPANY_LOGO / COMPANY_BILLING folded into the
  // profile / configuration / billing CRUD keys below.
  SETTINGS: {
    COMPANY_DELETE: 'perm-settings-company-delete',
    COMPANY_PROFILE_VIEW: 'perm-settings-company-profile-view',
    COMPANY_PROFILE_EDIT: 'perm-settings-company-profile-edit',
    COMPANY_PROFILE_LOGO_VIEW: 'perm-settings-company-profile-logo-view',
    COMPANY_PROFILE_LOGO_EDIT: 'perm-settings-company-profile-logo-edit',
    COMPANY_PROFILE_ADMIN_VIEW: 'perm-settings-company-profile-admin-view',
    COMPANY_PROFILE_ADMIN_EDIT: 'perm-settings-company-profile-admin-edit',
    COMPANY_PROFILE_PAYMENT_VIEW: 'perm-settings-company-profile-payment-view',
    COMPANY_PROFILE_PAYMENT_EDIT: 'perm-settings-company-profile-payment-edit',
    COMPANY_CONFIGURATION_VIEW: 'perm-settings-company-configuration-view',
    COMPANY_CONFIGURATION_EDIT: 'perm-settings-company-configuration-edit',
    PROJECT_ELEMENTS_VIEW: 'perm-settings-project-elements-view',
    PROJECT_ELEMENTS_EDIT: 'perm-settings-project-elements-edit',
    TEMPLATES_VIEW: 'perm-settings-templates-view',
    TEMPLATES_EDIT: 'perm-settings-templates-edit',
    BILLING_VIEW: 'perm-settings-billing-view',
    BILLING_EDIT: 'perm-settings-billing-edit',
    PROPOSAL_VIEW: 'perm-settings-proposal-view',
    PROPOSAL_EDIT: 'perm-settings-proposal-edit',
    NOTIFICATIONS_VIEW: 'perm-settings-notifications-view',
    NOTIFICATIONS_EDIT: 'perm-settings-notifications-edit',
    INDIVIDUAL: 'perm-settings-individual',
    // Sprint (prod-fixes) — gates the Website Lead Integration settings
    // section (WordPress webhook + field mapping). Owner-only by default;
    // grant explicitly to others via the assign-permission flow.
    WEB_LEAD_INTEGRATION: 'perm-settings-web-lead-integration',
    SQUARE_INTEGRATION: 'perm-settings-square-integration',
  },

  // ── Service Requests ──────────────────────────────────────
  SERVICE_REQUEST: {
    CREATE: 'perm-service-request-create',
    VIEW: 'perm-service-request-view',
    EDIT: 'perm-service-request-edit',
    DELETE: 'perm-service-request-delete',
  },

  // ── Leads ─────────────────────────────────────────────────
  LEAD: {
    CREATE: 'perm-lead-create',
    VIEW: 'perm-lead-view',
    EDIT: 'perm-lead-edit',
    DELETE: 'perm-lead-delete',
    // Authorize erasure of a lead's captured submitting IP (Owner / Marketing Manager).
    REDACT_IP: 'perm-lead-redact-ip',
  },

  // ── Marketing module ──────────────────────────────────────
  // RECOMMENDATION_REVIEW folded into MANAGE_CONNECTIONS.
  MARKETING: {
    VIEW: 'perm-marketing-view',
    MANAGE_CONNECTIONS: 'perm-marketing-manage-connections',
  },

  // ── Projects ──────────────────────────────────────────────
  // Production + project notes fold into VIEW / EDIT.
  PROJECT: {
    CREATE: 'perm-project-create',
    VIEW: 'perm-project-view',
    EDIT: 'perm-project-edit',
    DELETE: 'perm-project-delete',
    /** Sprint 43 — bulk CSV import (Owner + Admin default). */
    IMPORT_CSV: 'perm-project-import-csv',
    /** Sprint 43 — in-flight / existing project create (Owner + Admin default). */
    INFLIGHT: 'perm-project-inflight',
    /** Advanced. Owner always. Nobody else by default — merge two jobs on the same client. */
    MERGE: 'perm-project-merge',
  },

  // ── Users ─────────────────────────────────────────────────
  USER: {
    CREATE: 'perm-user-create',
    EDIT: 'perm-user-edit',
    VIEW: 'perm-user-view',
    DELETE: 'perm-user-delete',
    // Colleagues → Login tab: reset another user's password. Owner
    // always passes; Admin gets it by default. Grant explicitly to others.
    RESET_PASSWORD: 'perm-user-reset-password',
  },

  // ── Sales-rep assignment (advanced) ───────────────────────
  // Gates changing the assigned sales rep on a lead / client. Owner
  // always passes; Admin + Sales Manager get it by default. Also gates
  // unassigned-lead list visibility (with LEAD.CREATE). Replaces retired
  // perm-client-assign and perm-client-rep-reassign.
  ASSIGNMENT: {
    REASSIGN_REP: 'perm-assignment-reassign-rep',
  },

  // ── Locations ─────────────────────────────────────────────
  LOCATION: {
    CREATE: 'perm-location-create',
    VIEW: 'perm-location-view',
    EDIT: 'perm-location-edit',
    DELETE: 'perm-location-delete',
  },

  // ── Templates ─────────────────────────────────────────────
  TEMPLATE: {
    CREATE: 'perm-template-create',
    VIEW: 'perm-template-view',
    EDIT: 'perm-template-edit',
    DELETE: 'perm-template-delete',
  },

  // ── Project Element Templates ─────────────────────────────
  PROJECT_ELEMENT_TEMPLATE: {
    CREATE: 'perm-project-element-template-create',
    VIEW: 'perm-project-element-template-view',
    EDIT: 'perm-project-element-template-edit',
    DELETE: 'perm-project-element-template-delete',
  },

  // ── Activity Hub ──────────────────────────────────────────
  ACTIVITY_HUB: {
    VIEW: 'perm-activity-hub-view',
    CHAT: 'perm-activity-hub-chat',
    CHAT_SEND_TEXT: 'perm-activity-hub-chat-send-text',
    CHAT_SEND_IMAGE: 'perm-activity-hub-chat-send-image',
    CHAT_SEND_FILE: 'perm-activity-hub-chat-send-file',
    COMMUNICATION: 'perm-activity-hub-communication',
    COMMUNICATION_LOG_INTERACTION: 'perm-activity-hub-communication-log-interaction',
  },

  // ── Correspondence ────────────────────────────────────────
  // Gates the Correspondence surface (system sent/received email —
  // client invoice notifications and partner / COI threads; nav entry,
  // Activity Hub tab, /correspondence route, and the correspondence
  // endpoints). Owner + Admin get full CRUD by default; Estimator gets
  // VIEW/CREATE/EDIT to preserve the COI correspondence workflow.
  // Everyone else must be granted explicitly.
  CORRESPONDENCE: {
    VIEW: 'perm-correspondence-view',
    CREATE: 'perm-correspondence-create',
    EDIT: 'perm-correspondence-edit',
    DELETE: 'perm-correspondence-delete',
  },

  // ── Estimates ─────────────────────────────────────────────
  // APPROVE / DENY folded into MODIFY.
  ESTIMATE: {
    READ: 'perm-estimate-read',
    MODIFY: 'perm-estimate-modify',
  },

  // ── Products ─────────────────────────────────────────────
  // MANAGE_CUSTOM folded into CREATE / EDIT.
  PRODUCT: {
    VIEW: 'perm-product-view',
    CREATE: 'perm-product-create',
    EDIT: 'perm-product-edit',
    DELETE: 'perm-product-delete',
  },

  // Advanced — Owner + Estimator only by default [SHP-PRICE-3].
  PRICING_TABLES: {
    VIEW: 'perm-pricing-tables-view',
    EDIT: 'perm-pricing-tables-edit',
  },

  // ── Services ────────────────────────────────────────────
  // MANAGE_CUSTOM folded into CREATE / EDIT.
  SERVICE: {
    VIEW: 'perm-service-view',
    CREATE: 'perm-service-create',
    EDIT: 'perm-service-edit',
    DELETE: 'perm-service-delete',
  },

  // ── Procurement ─────────────────────────────────────────
  // Advanced keys stay — do not fold into CRUD.
  PROCUREMENT: {
    VIEW: 'perm-procurement-view',
    MANAGE: 'perm-procurement-manage',
    APPROVE: 'perm-procurement-approve',
    INVOICE_UPLOAD: 'perm-procurement-invoice-upload',
    SKU_MAPPING_APPROVE: 'perm-procurement-sku-mapping-approve',
    MAPPING_UNLOCK: 'perm-procurement-mapping-unlock',
  },

  // ── Rewards ─────────────────────────────────────────────
  // Must stay byte-for-byte identical to the backend REWARDS block.
  // Owner-only by default; granted to other users via the admin
  // assign-permission flow. Invoice upload / vendor assign fold into MANAGE.
  REWARDS: {
    VIEW: 'perm-rewards-view',
    MANAGE: 'perm-rewards-manage',
  },

  // ── Partners ──────────────────────────────────────────
  // Compliance / financial / budget fold into VIEW / EDIT.
  PARTNER: {
    VIEW: 'perm-partner-view',
    CREATE: 'perm-partner-create',
    EDIT: 'perm-partner-edit',
    DELETE: 'perm-partner-delete',
  },

  // ── Budget ───────────────────────────────────────────
  BUDGET: {
    VIEW: 'perm-budget-view',
    EDIT: 'perm-budget-edit',
    // Budget Analysis & Upgrade — gates OVERHEAD/MARGIN visibility. The
    // server redacts profit lines for users without it; the payload's
    // `canViewFinancials` flag reflects this permission.
    VIEW_FINANCIALS: 'perm-budget-view-financials',
  },

  // ── Contracts ─────────────────────────────────────────
  // CREATE / EDIT / SIGN / GENERATE_PDF folded into VIEW.
  // Countersign is Owner or the authorized-signer roster, not a key.
  CONTRACT: {
    VIEW: 'perm-contract-view',
    EDIT_LEGAL: 'perm-contract-edit-legal',
  },

  // ── Reports ───────────────────────────────────────────────
  REPORT: {
    VIEW: 'perm-report-view',
    GENERATE: 'perm-report-generate',
    EXPORT: 'perm-report-export',
  },

  // ── Lark (product brain). Voice / writes / leave-site nudges.
  // Tenant admin may grant/revoke this key only — not arms, personality,
  // or the scoreboard ([LARK-BRAIN-1]).
  LARK: {
    USE: 'perm-lark-use',
  },

  // ── Tasks v2 (design proposal §4.1, Slice 1.1.D) ─────────
  // Mirrors `C-Back-End/src/common/constants/permissions.ts::TASK`.
  // Distinct from project notes / interactions and intentionally
  // disjoint from the legacy Project Fulfillment surface (backed by
  // ProjectTask). VIEW_OWN is the assigned-to-me default; VIEW_ALL is
  // the Owner / admin lens; ASSIGN gates create/update/delete.
  TASK: {
    VIEW_OWN: 'perm-task-view-own',
    VIEW_ALL: 'perm-task-view-all',
    ASSIGN: 'perm-task-assign',
    TEMPLATE_MANAGE: 'perm-task-template-manage',
  },

  // ── Weekly Report (Slice 1.6.A schema, Phase 2 logic) ────
  WEEKLY_REPORT: {
    MANAGE: 'perm-weekly-report-manage',
    VIEW_ALL: 'perm-weekly-report-view-all',
    ADMIN: 'perm-weekly-report-admin',
  },
} as const;

function collectPermissionValues(obj: Record<string, unknown>): string[] {
  const values: string[] = [];
  for (const val of Object.values(obj)) {
    if (typeof val === 'string') {
      values.push(val);
    } else if (typeof val === 'object' && val !== null) {
      values.push(...collectPermissionValues(val as Record<string, unknown>));
    }
  }
  return values;
}

/** Flat live catalog — used to filter Colleagues matrix options. */
export const ALL_PERMISSION_VALUES: readonly string[] = collectPermissionValues(
  PERMISSIONS as unknown as Record<string, unknown>,
);
